import * as React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent,
  ChartLegend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
  BarChart,
  Bar
} from '../ui/chart'

export interface DataPoint {
  time: string
  [key: string]: string | number
}

export interface ChartMetric {
  key: string
  label: string
  color?: 'gold' | 'purple' | 'blue' | 'green' | 'orange'
  type?: 'line' | 'area' | 'bar'
}

export interface PerformanceChartProps {
  title: string
  description?: string
  data: DataPoint[]
  metrics: ChartMetric[]
  type?: 'line' | 'area' | 'bar'
  height?: number
  className?: string
}

const colorMap = {
  gold: '#f5b800',
  purple: '#9333ea',
  blue: '#3b82f6',
  green: '#10b981',
  orange: '#f59e0b'
}

const defaultColors = ['gold', 'purple', 'blue', 'green', 'orange'] as const

export const PerformanceChart = React.forwardRef<HTMLDivElement, PerformanceChartProps>(
  ({ title, description, data, metrics, type = 'line', height = 300, className, ...props }, ref) => {
    const chartConfig = React.useMemo(() => {
      return metrics.reduce((config, metric, index) => {
        const color = metric.color || defaultColors[index % defaultColors.length]
        config[metric.key] = {
          label: metric.label,
          color: colorMap[color]
        }
        return config
      }, {} as Record<string, { label: string; color: string }>)
    }, [metrics])

    const renderChart = () => {
      switch (type) {
        case 'area':
          return (
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="time" className="text-xs" axisLine={false} tickLine={false} />
              <YAxis className="text-xs" axisLine={false} tickLine={false} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend />
              {metrics.map((metric, index) => {
                const color = metric.color || defaultColors[index % defaultColors.length]
                return (
                  <Area
                    key={metric.key}
                    type="monotone"
                    dataKey={metric.key}
                    stroke={colorMap[color]}
                    fill={colorMap[color]}
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                )
              })}
            </AreaChart>
          )
        
        case 'bar':
          return (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="time" className="text-xs" axisLine={false} tickLine={false} />
              <YAxis className="text-xs" axisLine={false} tickLine={false} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend />
              {metrics.map((metric, index) => {
                const color = metric.color || defaultColors[index % defaultColors.length]
                return (
                  <Bar
                    key={metric.key}
                    dataKey={metric.key}
                    fill={colorMap[color]}
                  />
                )
              })}
            </BarChart>
          )
        
        default: // line
          return (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="time" className="text-xs" axisLine={false} tickLine={false} />
              <YAxis className="text-xs" axisLine={false} tickLine={false} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend />
              {metrics.map((metric, index) => {
                const color = metric.color || defaultColors[index % defaultColors.length]
                return (
                  <Line
                    key={metric.key}
                    type="monotone"
                    dataKey={metric.key}
                    stroke={colorMap[color]}
                    strokeWidth={2}
                    dot={{ fill: colorMap[color], strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: colorMap[color], strokeWidth: 2 }}
                  />
                )
              })}
            </LineChart>
          )
      }
    }

    return (
      <Card ref={ref} className={className} {...props}>
        <CardHeader>
          <CardTitle className="text-gradient-gold-purple">{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={chartConfig}
            className={`h-[${height}px] w-full`}
          >
            {renderChart()}
          </ChartContainer>
        </CardContent>
      </Card>
    )
  }
)

PerformanceChart.displayName = 'PerformanceChart'
